import React, { useCallback, useState, useEffect, useRef } from 'react';
import { TokenSource, TokenState, TokenCategory, TokenStatus } from '../stores/data/types'
import { useUIStore } from '../stores/ui/uiStore';
import { useToasterStore } from '../stores/ui/toasterStore';
import { useGlobalDeckFilterStore } from '../stores/ui/globalDeckFilterStore';
import { formatNumber, formatElapsedTime, formatNumberToLargestUnit } from '../utils/formatters';
import RankButton from './RankButton';
import ImageTooltip from './ImageTooltip';
import McTooltip from './McTooltip';
import CopyButton from './CopyButton';
import CategoryChip from './CategoryChip';
import StatusChip from './StatusChip';
import { useDataChangeDetection } from '../hooks/useDataChangeDetection';
import { feManagementSystemApi } from '../api/fe-management-system-api';
import getApiErrorMessage from '../utils/getApiErrorMessage';
import RetrospectiveTypeChip from './RetrospectiveTypeChip';


type SocialLinkType = 'twitter_tweet' | 'twitter_profile' | 'twitter_community' | 'telegram' | 'instagram' | 'reddit' | 'github' | 'tiktok' | 'others';
type SocialLink = { type: SocialLinkType; href: string };

const TWITTER_HOSTS = new Set(['x.com', 'www.x.com', 'twitter.com', 'www.twitter.com']);
const TELEGRAM_HOSTS = new Set(['t.me', 'www.t.me', 'telegram.me', 'www.telegram.me', 'telegram.org', 'www.telegram.org']);
const INSTAGRAM_HOSTS = new Set(['instagram.com', 'www.instagram.com']);
const REDDIT_HOSTS = new Set([
  'reddit.com', 'www.reddit.com',
  'old.reddit.com', 'new.reddit.com', 'np.reddit.com',
  'redd.it', 'www.redd.it',
]);
const GITHUB_HOSTS = new Set(['github.com', 'www.github.com', 'gist.github.com']);
const TIKTOK_HOSTS = new Set(['tiktok.com', 'www.tiktok.com', 'vm.tiktok.com', 'm.tiktok.com']);

const SOCIAL_ICON_SIZE = 'w-3.5 h-3.5';

const renderSocialIcon = (type: SocialLinkType): React.ReactNode => {
  switch (type) {
    case 'twitter_tweet':
      return <img src="/x.png" alt="tweet" className={SOCIAL_ICON_SIZE} />;
    case 'twitter_profile':
      return (
        <svg className={`${SOCIAL_ICON_SIZE} text-gray-300`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-label="twitter profile">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
        </svg>
      );
    case 'twitter_community':
      return (
        <svg className={`${SOCIAL_ICON_SIZE} text-gray-300`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-label="twitter community">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
        </svg>
      );
    case 'telegram':
      return <img src="/telegram.png" alt="telegram" className={SOCIAL_ICON_SIZE} />;
    case 'instagram':
      return <img src="/insta.png" alt="instagram" className={SOCIAL_ICON_SIZE} />;
    case 'reddit':
      return <img src="/reddit.png" alt="reddit" className={`${SOCIAL_ICON_SIZE} scale-[1.4]`} />;
    case 'github':
      return <img src="/github.png" alt="github" className={SOCIAL_ICON_SIZE} />;
    case 'tiktok':
      return <img src="/ticktok.png" alt="tiktok" className={`${SOCIAL_ICON_SIZE} scale-[2]`} />;
    case 'others':
      return <img src="/website.png" alt="website" className={SOCIAL_ICON_SIZE} />;
  }
};

function classifyTwitterPath(pathname: string): 'twitter_tweet' | 'twitter_profile' | 'twitter_community' {
  const lower = pathname.toLowerCase();
  if (lower.startsWith('/i/communities/')) return 'twitter_community';
  const segments = lower.split('/').filter(Boolean);
  if (segments.length >= 3 && segments[1] === 'status') return 'twitter_tweet';
  return 'twitter_profile';
}

function classifySocialUrl(url: string | undefined | null): SocialLink | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  const host = parsed.hostname.toLowerCase();
  if (TWITTER_HOSTS.has(host)) return { type: classifyTwitterPath(parsed.pathname), href: url };
  if (TELEGRAM_HOSTS.has(host)) return { type: 'telegram', href: url };
  if (INSTAGRAM_HOSTS.has(host)) return { type: 'instagram', href: url };
  if (REDDIT_HOSTS.has(host)) return { type: 'reddit', href: url };
  if (GITHUB_HOSTS.has(host)) return { type: 'github', href: url };
  if (TIKTOK_HOSTS.has(host)) return { type: 'tiktok', href: url };
  return { type: 'others', href: url };
}

function buildSocialLinks(tokenInfo: { twitter?: string; website?: string; telegram?: string } | undefined): SocialLink[] {
  if (!tokenInfo) return [];
  const result: SocialLink[] = [];
  const seen = new Set<string>();
  for (const raw of [tokenInfo.twitter, tokenInfo.website, tokenInfo.telegram]) {
    const link = classifySocialUrl(raw);
    if (!link) continue;
    if (seen.has(link.href)) continue;
    seen.add(link.href);
    result.push(link);
  }
  return result;
}


interface PulseCardProps {
  data: TokenState;
  style?: React.CSSProperties;
}

const PulseCard: React.FC<PulseCardProps> = React.memo(({ data, style = {} }) => {
  const [isTextEditMode, setIsTextEditMode] = useState(false);
  const [memoText, setMemoText] = useState('');
  const [isMemoExpanded, setIsMemoExpanded] = useState(false);
  const { selectedToken, openCreateNoteModal, setSelectedToken, openTokenDetailModal, openSocialDetailModal, openNoteDetailModal } = useUIStore();
  const { isRetrospectiveTokenMode, retrospectiveTokens } = useGlobalDeckFilterStore();
  const { showToaster } = useToasterStore();
  const { isMetricsChanged } = useDataChangeDetection(data);

  // currentTokenAmountInDecimals 색깔 상태 관리
  const [tokenAmountColor, setTokenAmountColor] = useState<'green' | 'red' | null>(null);
  const previousTokenAmountRef = useRef<number | null>(null);

  // FE_TOKEN_STATE에서 카테고리 가져오기 (기본값: NONE = 'None')
  const selectedCategory = data.feTokenState?.category || TokenCategory.NONE;
  const selectedStatus = data.feTokenState?.status || TokenStatus.NONE;
  const restrictedSocialActions = [
    TokenStatus.NONE,
    TokenStatus.FETCHING_DATA,
    TokenStatus.LATER,
  ];
  const canOpenSocialNote = !restrictedSocialActions.includes(selectedStatus);

  // 메모 텍스트 초기화
  useEffect(() => {
    setMemoText(data.feTokenState?.memo || '');
  }, [data.feTokenState?.memo]);

  // currentTokenAmountInDecimals 변경 감지 및 색깔 결정
  useEffect(() => {
    const currentAmount = data.statistics.currentTokenAmountInDecimals;

    if (previousTokenAmountRef.current === null) {
      previousTokenAmountRef.current = currentAmount;
      return;
    }

    const previousAmount = previousTokenAmountRef.current;

    if (currentAmount > previousAmount) {
      setTokenAmountColor('green');
    } else if (currentAmount < previousAmount) {
      setTokenAmountColor('red');
    }
    // 같으면 색깔 유지 (다음 업데이트까지)

    previousTokenAmountRef.current = currentAmount;
  }, [data.statistics.currentTokenAmountInDecimals]);

  const handleCardClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(data.tokenAddress);
      showToaster(`$${data.tokenInfo.tokenSymbol} contract address copied to clipboard!`, 'success');
    } catch (err) {
      console.error('클립보드 복사 실패:', err);
      showToaster('Failed to copy to clipboard.', 'error');
    }
    setSelectedToken({
      tokenAddress: data.tokenAddress,
      symbol: data.tokenInfo.tokenSymbol || 'Unknown',
    });
    openTokenDetailModal();
  };

  const handleOpenSocialDetail = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedToken({
      tokenAddress: data.tokenAddress,
      symbol: data.tokenInfo.tokenSymbol || 'Unknown',
    });
    openSocialDetailModal();
  };

  const handleOpenNoteDetail = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedToken({
      tokenAddress: data.tokenAddress,
      symbol: data.tokenInfo.tokenSymbol || 'Unknown',
    });
    openNoteDetailModal();
  };


  const handleTrashClick = async () => {
    try {
       await feManagementSystemApi.updateTokenState(data.tokenAddress, {
        isArchived: true,
      });
      showToaster(`$${data.tokenInfo.tokenSymbol} has been archived.`, 'success');
    } catch (error: any) {
      console.error('Failed to archive token:', error);
      showToaster(
        getApiErrorMessage(error, 'Failed to archive.'),
        'error'
      );
    }
  };

  const handleAxiomClick = () => {
    if (!data.tokenInfo.poolAddress) {
      return;
    }
    const axiomUrl = `https://axiom.trade/meme/${data.tokenInfo.poolAddress}`;
    window.open(axiomUrl, '_blank');
  };

  const handleGmgnClick = () => {
    const link = `http://gmgn.ai/sol/token/solscan_${data.tokenAddress}`;
    window.open(link, '_blank');
  };

  const handleMemoSave = async () => {
    try {
      await feManagementSystemApi.updateTokenState(data.tokenAddress, {
        memo: memoText,
      });
      setIsTextEditMode(false);
      setIsMemoExpanded(false);
      showToaster('Note saved!', 'success');
      // FE_TOKEN_STATE 스트림을 통해 자동으로 업데이트됨
    } catch (error: any) {
      console.error('메모 저장 실패:', error);
      showToaster(
        getApiErrorMessage(error, 'Failed to save note.'),
        'error'
      );
    }
  };

  const handleMemoCancel = () => {
    setMemoText(data.feTokenState?.memo || '');
    setIsTextEditMode(false);
  };

  // 랭크 데이터가 기본값인지 확인하는 함수
  const isDefaultRankData = () => {
    return data.ranking.lastUpdated.getTime() === 0;
  };

  // 데이터에서 필요한 값들을 추출
  const symbol = data.tokenInfo.tokenSymbol || 'Unknown';
  const name = data.tokenInfo.tokenName || 'Unknown';
  const imgUrl = data.tokenInfo.imageUrl;
  const tokenSource = data.tokenInfo.tokenSource;

  // 애니메이션 클래스 결정
  const getAnimationClass = useCallback(() => {
    if (isMetricsChanged) {
      return 'animate-pulse-green';
    }
    return '';
  }, [isMetricsChanged]);

  // 기본 테두리 클래스
  const retrospectiveTypes = isRetrospectiveTokenMode()
    ? retrospectiveTokens[data.tokenAddress]?.retrospectiveTypes ?? []
    : [];
  const showRetrospectiveChips = retrospectiveTypes.length > 0;

  const getBorderClass = () => {
    if (selectedToken?.tokenAddress === data.tokenAddress) {
      return 'border-red-500 hover:border-red-400';
    }
    return 'border-gray-700 hover:border-blue-500';
  };

  return (
    <>
      <div
        className={`rounded-lg border transition-all duration-300 ease-in-out mb-3 ${getBorderClass()} ${getAnimationClass()} ${
          'bg-gray-800'
        }`}
        onClick={handleCardClick}
        style={style}
      >
        <div className="flex p-3 pt-2 pb-0 justify-start mb-1 space-x-3">
          <div className="flex-shrink-0 pt-1">
            <ImageTooltip
              imageUrl={imgUrl || ''}
              width={128}
              height={128}
              delay={300}
            >
              <div className="relative">
                <div
                  className="w-16 h-16 rounded-lg border border-gray-600 flex items-center justify-center overflow-hidden bg-cover bg-center cursor-zoom-in"
                  style={{
                    backgroundImage: `url(${imgUrl})`
                  }}
                >
                </div>
                <div className={`absolute w-3 h-3 rounded-md ${tokenSource === TokenSource.PUMPFUN ? 'bg-green-500' :
                  tokenSource === TokenSource.LAUNCHLAB ? 'bg-orange-500' :
                    'bg-gray-500'
                  }`} style={{
                    bottom: -3,
                    right: -3
                  }}></div>
              </div>
            </ImageTooltip>
          </div>
          <div className="w-full">
            <div className="flex items-center justify-between mb-1 pt-1">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <h3 className="text-sm font-semibold text-white truncate max-w-[64px]">{symbol}</h3>
                  {data.hasOrderIssue && (
                    <div className="flex items-center" title="Order Issue가 있습니다">
                      <svg
                        className="w-4 h-4 text-yellow-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <span className="text-xs text-gray-400 truncate max-w-[55px]" title={name}>
                    {name}
                  </span>
                  <div className="text-[10px] flex items-center space-x-1 flex-shrink-0">
                    <div className="text-gray-400">
                      {data.tokenAddress.slice(0, 4)}...{data.tokenAddress.slice(-4)}
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <CopyButton
                        text={data.tokenAddress}
                        title="토큰 주소 복사"
                        onCopy={() => showToaster(`$${symbol} contract address copied to clipboard!`, 'success')}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2 flex-shrink-0">
                <div className="text-[10px] text-gray-400">
                  {formatElapsedTime(data.tokenInfo.firstTransferAt || Date.now())}
                </div>
                <div className="flex items-center space-x-1">
                  <CategoryChip category={selectedCategory} />
                  <StatusChip
                    tokenAddress={data.tokenAddress}
                    status={selectedStatus}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-baseline justify-between">
              <div className="flex-1">
                <div className="items-center mb-1">
                  <div className="flex items-center space-x-2 mr-2" style={{
                    marginLeft: -4,
                    marginTop: -4,
                    marginBottom: 1
                  }}>
                    <RankButton
                      value={data.ranking.cumulativeConvictionAtSameTimeRank}
                      rankType="cumulative"
                      isDefaultValue={isDefaultRankData()}
                    />
                    {data.feTokenState?.totalScore !== undefined && data.feTokenState?.totalScore !== 0 && (
                      <div 
                        className={`text-[11px] font-bold px-1.5 py-0.5 rounded border ${
                          data.feTokenState.totalScore > 20
                            ? 'bg-green-500/10 text-green-400 border-green-500/20'
                            : data.feTokenState.totalScore >= 14
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            : 'bg-red-500/10 text-red-400 border-red-500/20'
                        }`}
                        title="Evaluation Total Score"
                      >
                        {data.feTokenState.totalScore}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-2.5 h-2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                      </svg>
                      <span className="text-[11px] text-white">
                        {data.statistics.currentAfCnt} / {data.statistics.athAfCnt} <span className="text-gray-400 text-[9px]">({(data.statistics.currentAfCnt / data.statistics.athAfCnt * 100).toFixed(0)}%)</span>
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-2.5 h-2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
                      </svg>
                      <span className="text-[11px] text-white">
                        <span className={tokenAmountColor === 'green' ? 'text-green-500' : tokenAmountColor === 'red' ? 'text-red-500' : 'text-white'}>
                          {formatNumber(data.statistics.currentTokenAmountInDecimals)}
                        </span>
                        {' / '}
                        {formatNumber(data.statistics.athTokenAmountInDecimals)}
                        <span className="text-gray-400 text-[9px]">({(data.statistics.currentTokenAmountInDecimals / data.statistics.athTokenAmountInDecimals * 100).toFixed(0)}%)</span>
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-3 h-3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>
                      <span className="text-[11px] text-white">{formatNumber(data.statistics.totalUsedUsd)}</span>
                    </div>

                  </div>
                </div>
                {isTextEditMode ? (
                  <div className="rounded-md border-gray-700 text-[12px] border-t pt-2 mb-1 min-h-[20px]">
                    <textarea
                      value={memoText}
                      onChange={(e) => setMemoText(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full bg-gray-800 text-gray-300 border border-gray-600 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                      rows={3}
                      maxLength={100}
                      placeholder="메모를 입력하세요 (최대 100자)"
                    />
                    <div className="flex items-center justify-end space-x-2 mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMemoCancel();
                        }}
                        className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
                      >
                        취소
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMemoSave();
                        }}
                        className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                        저장
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`w-[280px] border-gray-700 text-[12px] text-gray-300 border-t py-1 pl-1 mb-1 min-h-[26px] cursor-pointer hover:bg-gray-900 transition-colors ${
                      !isMemoExpanded ? 'truncate' : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const memo = data.feTokenState?.memo || '';
                      // 메모가 있고 expand 가능한 경우 (긴 텍스트인 경우)
                      if (memo) {
                        setIsMemoExpanded(!isMemoExpanded);
                      } else {
                        // 메모가 없으면 편집 모드로 전환
                        setIsTextEditMode(true);
                      }
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setIsTextEditMode(true);
                    }}
                    style={{
                      maxWidth: '100%',
                      whiteSpace: isMemoExpanded ? 'normal' : 'nowrap',
                      overflow: isMemoExpanded ? 'visible' : 'hidden',
                      textOverflow: isMemoExpanded ? 'clip' : 'ellipsis',
                      wordBreak: isMemoExpanded ? 'break-word' : 'normal',
                    }}
                    title={isMemoExpanded ? '' : (data.feTokenState?.memo || '')}
                  >
                    {memoText || ''}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end space-x-3">
                <div className="text-right">
                  {(() => {
                    const currentMc = data.priceTracker?.mcap || data.statistics.lastFlow.mc;
                    const athMc = data.priceTracker?.ath_mcap || data.statistics.athMc;

                    return (
                      <>
                        <McTooltip
                          currentMc={currentMc}
                          athMc={athMc}
                        >
                          <div className="text-xs flex items-center space-x-1 justify-end">
                            {/* INFLOW/OUTFLOW 방향 표시 삼각형 */}
                            {data.statistics.lastFlow.flowType === 'INFLOW' ? (
                              <div className="w-0 h-0 border-l-[4px] border-r-[4px] border-b-[6px] border-l-transparent border-r-transparent border-b-green-500"></div>
                            ) : data.statistics.lastFlow.flowType === 'OUTFLOW' ? (
                              <div className="w-0 h-0 border-l-[4px] border-r-[4px] border-t-[6px] border-l-transparent border-r-transparent border-t-red-500"></div>
                            ) : null}

                            <span className="text-amber-400">
                              ${formatNumberToLargestUnit(currentMc)}
                            </span>
                          </div>
                        </McTooltip>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
        {showRetrospectiveChips && (
          <div className="flex flex-wrap gap-1 px-3 py-2 bg-gray-900">
            {retrospectiveTypes.map((type) => (
              <RetrospectiveTypeChip
                key={`${data.tokenAddress}-${type}`}
                type={type}
                className="text-[11px]"
              />
            ))}
          </div>
        )}
        <div className="flex rounded-b-lg shadow-[inset_0_3px_3px_-2px_rgba(0,0,0,0.1)] px-3 py-1 bg-slate-900 items-center justify-between w-full space-x-1">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1">
              <button
                className={`w-4 h-4 rounded-sm flex items-center justify-center transition-opacity ${data.tokenInfo.poolAddress
                  ? 'opacity-70 hover:opacity-100 cursor-pointer'
                  : 'opacity-5 cursor-not-allowed'
                  }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAxiomClick();
                }}
                disabled={!data.tokenInfo.poolAddress}
              >
                <div className="w-4 h-4 rounded-sm overflow-hidden bg-white flex items-center justify-center">
                  <img src="/axiom.png" alt="axiom" className="w-4 h-4" />
                </div>
              </button>
              <button
                className="w-4 h-4 rounded-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  handleGmgnClick();
                }}
              >
                <img src="/gmgn.png" alt="solscan" className="w-4 h-4" />
              </button>
            </div>
            {/* Divider */}
            <div className="w-px h-3 border-r border-gray-600"></div>
            <div className="flex items-center space-x-1">
              {buildSocialLinks(data.tokenInfo).map((link) => (
                <button
                  key={link.href}
                  type="button"
                  className={`${SOCIAL_ICON_SIZE} rounded-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity`}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(link.href, '_blank', 'noopener,noreferrer');
                  }}
                  title={link.href}
                >
                  {renderSocialIcon(link.type)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-4" style={{}}>
            {/* <button
              className="text-gray-400 hover:text-blue-400 transition-colors"
              style={{
                width: "18px",
                height: "18px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleStartInvestigationSync();
              }}
              title="토큰 조사 싱크 시작"
            >
              <svg style={{ width: 18, height: 'auto', }} xmlns="http://www.w3.org/2000/svg" fill="currentColor" xmlnsXlink="http://www.w3.org/1999/xlink" version="1.1" id="mdi-database-sync-outline" width="24" height="24" viewBox="0 0 24 24"><path d="M12 16C12.41 16 12.81 15.97 13.21 15.94C13.4 15.18 13.72 14.46 14.16 13.83C13.47 13.94 12.74 14 12 14C9.58 14 7.3 13.4 6 12.45V9.64C7.47 10.47 9.61 11 12 11S16.53 10.47 18 9.64V11.19C18.5 11.07 19 11 19.55 11C19.7 11 19.85 11 20 11.03V7C20 4.79 16.42 3 12 3S4 4.79 4 7V17C4 19.21 7.59 21 12 21C12.66 21 13.31 20.96 13.92 20.88C13.57 20.29 13.31 19.64 13.16 18.94C12.79 19 12.41 19 12 19C8.13 19 6 17.5 6 17V14.77C7.61 15.55 9.72 16 12 16M12 5C15.87 5 18 6.5 18 7S15.87 9 12 9 6 7.5 6 7 8.13 5 12 5M23 17.5C23 18.32 22.75 19.08 22.33 19.71L21.24 18.62C21.41 18.28 21.5 17.9 21.5 17.5C21.5 16.12 20.38 15 19 15V16.5L16.75 14.25L19 12V13.5C21.21 13.5 23 15.29 23 17.5M19 18.5L21.25 20.75L19 23V21.5C16.79 21.5 15 19.71 15 17.5C15 16.68 15.25 15.92 15.67 15.29L16.76 16.38C16.59 16.72 16.5 17.1 16.5 17.5C16.5 18.88 17.62 20 19 20V18.5Z" /></svg>
            </button> */}
            {canOpenSocialNote && (
              <div className="flex items-center space-x-3" style={{ marginRight: -2 }}>
                <button
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                  onClick={(e) => handleOpenSocialDetail(e)}
                  title="소셜 상세"
                >
               <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 50 50" className="size-4 fill-current">
                <path d="M 6.9199219 6 L 21.136719 26.726562 L 6.2285156 44 L 9.40625 44 L 22.544922 28.777344 L 32.986328 44 L 43 44 L 28.123047 22.3125 L 42.203125 6 L 39.027344 6 L 26.716797 20.261719 L 16.933594 6 L 6.9199219 6 z"></path>
                </svg>
                </button>
                <button
                  className="text-gray-400 hover:text-white transition-colors"
                  onClick={(e) => handleOpenNoteDetail(e)}
                  title="노트 상세"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                  </svg>
                </button>
              </div>
            )}
            {!canOpenSocialNote && (
              <button
                  className="transition-colors text-gray-400 hover:text-blue-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    openCreateNoteModal({
                      tokenSymbol: data.tokenInfo?.tokenSymbol || 'Unknown',
                      tokenAddress: data.tokenAddress,
                      tokenName: data.tokenInfo?.tokenName,
                      tokenTwitter: data.tokenInfo?.twitter,
                      tokenWebsite: data.tokenInfo?.website,
                      tokenCategory: data.feTokenState?.category,
                    });
                  }}
                  title={'트위터 싱크 설정'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="size-3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                  </svg>
                </button>
            )}
            <button
              className="text-gray-400 hover:text-red-400 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                handleTrashClick();
              }}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}, (prevProps, nextProps) => {
  // 데이터가 실제로 변경되었는지 비교
  // tokenAddress가 같고, 실제 데이터 내용이 같으면 리렌더링하지 않음
  if (prevProps.data.tokenAddress !== nextProps.data.tokenAddress) {
    return false; // 다른 토큰이면 리렌더링
  }

  // 같은 토큰이면 실제 데이터 변경 여부만 확인
  const prev = prevProps.data;
  const next = nextProps.data;

  // 주요 데이터 필드 비교 (실제로 화면에 표시되는 값들)
  return (
    prev.tokenInfo.tokenSymbol === next.tokenInfo.tokenSymbol &&
    prev.tokenInfo.tokenName === next.tokenInfo.tokenName &&
    prev.tokenInfo.imageUrl === next.tokenInfo.imageUrl &&
    prev.statistics.currentAfCnt === next.statistics.currentAfCnt &&
    prev.statistics.athAfCnt === next.statistics.athAfCnt &&
    prev.statistics.currentTokenAmountInDecimals === next.statistics.currentTokenAmountInDecimals &&
    prev.statistics.athTokenAmountInDecimals === next.statistics.athTokenAmountInDecimals &&
    prev.statistics.totalUsedUsd === next.statistics.totalUsedUsd &&
    prev.statistics.lastFlow.mc === next.statistics.lastFlow.mc &&
    prev.priceTracker?.mcap === next.priceTracker?.mcap &&
    prev.priceTracker?.ath_mcap === next.priceTracker?.ath_mcap &&
    prev.ranking.cumulativeConvictionAtSameTimeRank === next.ranking.cumulativeConvictionAtSameTimeRank &&
    prev.hasOrderIssue === next.hasOrderIssue &&
    prev.feTokenState?.category === next.feTokenState?.category &&
    prev.feTokenState?.status === next.feTokenState?.status &&
    prev.feTokenState?.memo === next.feTokenState?.memo &&
    JSON.stringify(prev.feTokenAlert) === JSON.stringify(next.feTokenAlert) &&
    prevProps.style === nextProps.style
  );
});

PulseCard.displayName = 'PulseCard';

export default PulseCard;